import { useCallback, useState } from "react";
import { validateField, validateStep, stepSchemas } from "../validation";
import { useFormStore } from "../store/formStore";

/**
 * Step-level validation hook.
 *
 * `getFieldError` validates against live store data on every call, so errors
 * clear immediately when the user fixes a field — no stale error snapshots.
 */
export function useStepValidation() {
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const validate = useCallback((step: number) => {
    const data = useFormStore.getState().data;
    return validateStep(step, data);
  }, []);

  const getFieldError = useCallback(
    (field: string): string | null => {
      if (!touchedFields.has(field)) return null;
      // Validate against live store data — errors clear as the user types
      const { data, currentStep } = useFormStore.getState();
      const schema = stepSchemas[currentStep];
      if (!schema) return null;
      const fields = typeof schema === "function" ? schema(data) : schema;
      const validators = fields[field];
      if (!validators) return null;
      const value = field.split(".").reduce<unknown>(
        (obj, key) =>
          obj != null && typeof obj === "object" ? (obj as Record<string, unknown>)[key] : undefined,
        data,
      );
      return validateField(value, validators);
    },
    [touchedFields],
  );

  const touchField = useCallback((field: string) => {
    setTouchedFields((prev) => new Set([...prev, field]));
  }, []);

  const touchAll = useCallback((step: number) => {
    const data = useFormStore.getState().data;
    const result = validateStep(step, data);
    setTouchedFields(new Set(Object.keys(result.errors)));
    return result;
  }, []);

  const clearErrors = useCallback((fields?: string[]) => {
    if (!fields) {
      setTouchedFields(new Set());
    } else {
      setTouchedFields((prev) => {
        const next = new Set(prev);
        for (const f of fields) next.delete(f);
        return next;
      });
    }
  }, []);

  return {
    validate,
    getFieldError,
    touchField,
    touchAll,
    clearErrors,
  };
}
